
import { Component } from 'react'
import * as styles from 'components/features/socialFeed/FeedPostCommentsOrdering/FeedPostCommentsOrdering.styl'
import * as cn from 'classnames'
import { ActionMenu } from 'components/features/pfm/ActionMenu/ActionMenu'
import { ActionMenuItem } from 'components/features/pfm/ActionMenu/ActionMenuItem'
import { MapDispatchToPropsFunction } from 'react-redux'
import {
  FeedOrdering,
  FEED_ORDERING_BY_POPULARITY,
  FEED_ORDERING_BY_CREATED,
  FEED_ORDERING_CHRONOLOGICALLY,
  WithPostUid,
  FEED_ORDERING_RANK,
} from 'interfaces/feed'
import { State } from 'interfaces/state'
import { createFeedPostSelector } from 'selectors/feedSelectors'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { getCommentsActionCreator } from 'actions/feedActions'
import { connectOptionally, createOptionalRenderer, OptionalRendererProps } from 'tangle-shared'
import Spinner from 'components/features/pfm/Spinner/Spinner'

const ORDERING_BY_LIKES_COUNT_TITLE = 'Most Popular'
const ORDERING_BY_CREATED_TITLE = 'Most Recent'
const ORDERING_CHRONOLOGICALLY = 'Chronological'
const ORDERING_RANK = 'By Rank'

const orderingTitleMap: Record<FeedOrdering, string> = {
  [FEED_ORDERING_BY_CREATED]: ORDERING_BY_CREATED_TITLE,
  [FEED_ORDERING_BY_POPULARITY]: ORDERING_BY_LIKES_COUNT_TITLE,
  [FEED_ORDERING_CHRONOLOGICALLY]: ORDERING_CHRONOLOGICALLY,
  [FEED_ORDERING_RANK]: ORDERING_RANK,
}

type FeedPostCommentsOrderingStateProps = {
  sortBy: FeedOrdering
  isLoading: boolean
}

type FeedPostCommentsOrderingDispatchProps = {
  onSort: (ordering: FeedOrdering) => void
}

type FeedPostCommentsOrderingOwnProps =
  & WithPostUid

type FeedPostCommentsOrderingProps =
  & FeedPostCommentsOrderingStateProps
  & FeedPostCommentsOrderingDispatchProps
  & FeedPostCommentsOrderingOwnProps

type FeedPostCommentsOrderingOwnState = {
  isShown: boolean
}

class FeedPostCommentsOrderingComponent extends Component<FeedPostCommentsOrderingProps, FeedPostCommentsOrderingOwnState> {
  state = {
    isShown: false,
  }

  render () {
    const { sortBy, isLoading } = this.props
    const { isShown } = this.state

    const menuClassName = cn(
      styles.sortMenu,
      {
        [styles.shown]: isShown,
      }
    )
    return (
      <div className={ styles.container }>
        { isLoading && (
          <Spinner />
        )}
        <div className={ styles.content } onClick={ this.handleShow }>
          Sort by: <div className={ styles.sortLink }>{ orderingTitleMap[sortBy] }</div>
        </div>
        <div className={ menuClassName }>
          <ActionMenu onHide={ this.handleHide }>
            <AnalyticsClick
              category='module-feed_post'
              action='toggle-comment_ordering'
              label={ ORDERING_BY_LIKES_COUNT_TITLE }
            >
              <ActionMenuItem
                isActive={ sortBy === FEED_ORDERING_BY_POPULARITY }
                onClick={ this.handleSort(FEED_ORDERING_BY_POPULARITY) }
              >
                { ORDERING_BY_LIKES_COUNT_TITLE }
              </ActionMenuItem>
            </AnalyticsClick>

            <AnalyticsClick
              category='module-feed_post'
              action='toggle-comment_ordering'
              label={ ORDERING_BY_CREATED_TITLE }
            >
              <ActionMenuItem
                isActive={ sortBy === FEED_ORDERING_BY_CREATED }
                onClick={ this.handleSort(FEED_ORDERING_BY_CREATED) }
              >
                { ORDERING_BY_CREATED_TITLE }
              </ActionMenuItem>
            </AnalyticsClick>

            <AnalyticsClick
              category='module-feed_post'
              action='toggle-comment_ordering'
              label={ ORDERING_CHRONOLOGICALLY }
            >
              <ActionMenuItem
                isActive={ sortBy === FEED_ORDERING_CHRONOLOGICALLY }
                onClick={ this.handleSort(FEED_ORDERING_CHRONOLOGICALLY) }
              >
                { ORDERING_CHRONOLOGICALLY }
              </ActionMenuItem>
            </AnalyticsClick>
          </ActionMenu>
        </div>
      </div>
    )
  }

  private handleSort = (nextSortBy: FeedOrdering) => () => {
    const { sortBy, onSort } = this.props
    this.handleHide()
    if (nextSortBy !== sortBy) {
      onSort(nextSortBy)
    }
  }

  private handleHide = () => {
    this.setState({ isShown: false })
  }

  private handleShow = () => {
    this.setState({ isShown: true })
  }
}

const createMapStateToProps = () => {
  const optionalRenderer = createOptionalRenderer<FeedPostCommentsOrderingStateProps>()
  const postSelector = createFeedPostSelector()

  return (state: State, ownProps: FeedPostCommentsOrderingOwnProps): OptionalRendererProps<FeedPostCommentsOrderingStateProps> => {
    const post = postSelector(state, ownProps)
    const isLoading = Boolean(state.app.feed.postCommentsLoadingIds[ownProps.postUid])

    if (!post) {
      return optionalRenderer.fallback()
    }

    return optionalRenderer.render({
      sortBy: post.commentsOrdering,
      isLoading,
    })
  }
}

const mapDispatchToProps: MapDispatchToPropsFunction<FeedPostCommentsOrderingDispatchProps, FeedPostCommentsOrderingOwnProps> =
  (dispatch, { postUid }) => ({
    onSort: (ordering: FeedOrdering) => dispatch(getCommentsActionCreator.request({ postUid, ordering })),
  })

const FeedPostCommentsOrdering = connectOptionally<
  FeedPostCommentsOrderingStateProps,
  FeedPostCommentsOrderingDispatchProps,
  FeedPostCommentsOrderingOwnProps
>(createMapStateToProps, mapDispatchToProps)(FeedPostCommentsOrderingComponent)

export default FeedPostCommentsOrdering
